import { type Ref, computed, onMounted, watch } from 'vue'

import { productAPI } from '@backmarket/http-api'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { MAX_REVIEWS_DISPLAYED_IN_LIST } from '~/scopes/reviews/constants'

import { useUrlParams } from './useUrlParams'

export function useProductTracking(
  product: Ref<productAPI.GetProductResponse | null>,
  offer: Ref<productAPI.GetBestOffersResponse[number] | null | undefined>,
  offers: Ref<productAPI.GetBestOffersResponse | null | undefined>,
  rating: Ref<Rating | null>,
) {
  const { offerType, grade } = useUrlParams()
  const { trackProductPage } = useTracking()

  const productEndpointData = computed(() => ({
    // Previously dealType depended on recommended pickers, which aren't ported to nuxt3 - so we have a static value
    dealType: 'normal',
    brand: product.value?.trackingDetails.brand ?? '',
    category: product.value?.trackingDetails.category3Name ?? '',
    color: product.value?.trackingDetails.color ?? '',
    id: Number(product.value?.legacyId),
    image: product.value?.images?.[0]?.url ?? '',
    isSwapEnabled: product.value?.isSwapEligible ?? false,
    model: product.value?.model ?? '',
    name: product.value?.rawTitle ?? '',
    uuid: product.value?.id ?? '',
    webUrl: product.value?.link?.href ?? '',
  }))

  const offerEndpointData = computed(() => ({
    currency: offer.value?.price?.currency ?? '',
    hasExpressShipping: !!offer.value?.reassurance?.shipping?.express,
    hasNewBattery: ['disabled', 'available'].includes(
      offer.value?.newBattery?.status ?? '',
    ),
    hasNewBatteryInStock: offer.value?.newBattery?.status === 'available',
    listingId: offer.value?.id ?? 0,
    merchantId: String(offer.value?.merchant?.id) ?? '',
    sellerOrigin:
      offer.value?.merchant?.countryCode ||
      offer.value?.merchant?.country ||
      '',
    price: offer.value?.price?.amount ?? '',
    shippingDelay: offer.value?.reassurance?.shipping?.free?.delayInHours ?? 0,
    stock: offer.value?.reassurance?.stock?.stockRawValue ?? 0,
    variant: offer.value?.backboxGrade?.value ?? 0,
  }))

  const rateEndpointData = computed(() => ({
    averageRate: rating.value?.averageRate ?? 0,
    hasReviews: (rating.value?.count ?? 0) > 0,
    numberTotalReviews: rating.value?.count ?? 0,
    numberReviewsDisplayed: Math.min(
      rating.value?.count ?? 0,
      MAX_REVIEWS_DISPLAYED_IN_LIST,
    ),
  }))

  const urlData = computed(() => ({
    mobileDeeplink: `backmarket://product/${
      product.value?.id
    }?specialOfferType=${offerType?.value ?? '0'}`,
    specialOfferType: offerType?.value ?? '0',
  }))

  // This is not yet set in front-apps, but in pastrami we get some information for the tracking
  // set in the session storage. It's used to know where the user comes from (reco, search, bundle...)
  const sessionStorageData = computed(() => {
    if (!product.value?.legacyId || !isBrowser()) {
      return { list: '' }
    }

    const item = sessionStorage.getItem(product.value?.legacyId)
    if (!isEmpty(item)) {
      return { list: JSON.parse(item as string).source }
    }

    return { list: '' }
  })

  const productTracking = computed(() => ({
    ...productEndpointData.value,
    ...offerEndpointData.value,
    ...rateEndpointData.value,
    ...urlData.value,
    ...sessionStorageData.value,
  }))

  const listingsTracking = computed(() =>
    (offers.value ?? []).map(({ id, backboxGrade = {}, price = {} }) => ({
      id,
      variant: backboxGrade.value,
      price: price.amount,
    })),
  )

  onMounted(() => {
    trackProductPage({
      product: productTracking.value,
      listings: listingsTracking.value,
    })
  })

  watch([grade, offerType], () => {
    trackProductPage({
      product: productTracking.value,
      listings: listingsTracking.value,
    })
  })

  return {
    product: productTracking,
    listings: listingsTracking,
  }
}
